import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useConfig } from "@/lib/useConfig";
import { LEAD_SOURCES } from "@/lib/rbac";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SUB_SOURCES = ["Direct Call", "Instagram", "WhatsApp Message", "WhatsApp API", "Walk-in", "Referral", "Other"];
const isCentralSource = (s) => (s || "").toLowerCase().includes("central sales");

// Popup shown when a lead is moved to Booked (or later) but required details are still missing.
export const CompleteLeadDialog = ({ open, onOpenChange, customerId, targetStatus, onCompleted }) => {
  const brands = useConfig("brands");
  const leadCfg = useConfig("lead-sources");
  const LEAD_LIST = leadCfg.length ? leadCfg.map((l) => l.name) : LEAD_SOURCES;
  const [cust, setCust] = useState(null);
  const [form, setForm] = useState({ make: "", model: "", lead_source: "", sub_source: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !customerId) return;
    api.get(`/customers/${customerId}`).then(({ data }) => {
      const c = data.customer;
      setCust(c);
      setForm({
        make: c.vehicle?.make || "",
        model: c.vehicle?.model || "",
        lead_source: c.lead_source || "",
        sub_source: c.sub_source || "",
      });
    });
  }, [open, customerId]);

  const central = isCentralSource(form.lead_source);
  const models = brands.find((b) => b.name === form.make)?.models || [];

  const save = async () => {
    if (!form.make || !form.model) return toast.error("Select car make and model");
    if (!central && !form.lead_source) return toast.error("Select a lead source");
    if (central && !form.sub_source) return toast.error("Select how this lead came in");
    setBusy(true);
    try {
      await api.put(`/customers/${customerId}`, {
        name: cust.name,
        phone: cust.phone,
        email: cust.email || "",
        notes: cust.notes || "",
        lead_source: central ? "Central Sales Team" : form.lead_source,
        sub_source: form.sub_source || "",
        studio_id: cust.studio_id || null,
        vehicle: { ...(cust.vehicle || {}), make: form.make, model: form.model },
      });
      onOpenChange(false);
      await onCompleted();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="complete-lead-dialog">
        <DialogHeader>
          <DialogTitle>Complete lead details</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">
          Before moving this lead to <b>{targetStatus}</b>, fill in the details below.
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Car make *</Label>
            <Select value={form.make} onValueChange={(v) => setForm((f) => ({ ...f, make: v, model: "" }))}>
              <SelectTrigger className="mt-1" data-testid="complete-make-select">
                <SelectValue placeholder="Select brand" />
              </SelectTrigger>
              <SelectContent>
                {brands.map((b) => (
                  <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Car model *</Label>
            <Select value={form.model} onValueChange={(v) => setForm((f) => ({ ...f, model: v }))}>
              <SelectTrigger className="mt-1" data-testid="complete-model-select">
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                {models.map((m) => (
                  <SelectItem key={m} value={m}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {central ? (
            <div>
              <Label className="text-xs">How did this lead come in? *</Label>
              <Select value={form.sub_source} onValueChange={(v) => setForm((f) => ({ ...f, sub_source: v }))}>
                <SelectTrigger className="mt-1" data-testid="complete-sub-source-select">
                  <SelectValue placeholder="Select sub-source" />
                </SelectTrigger>
                <SelectContent>
                  {SUB_SOURCES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-slate-400">Lead source: Central Sales Team (locked)</p>
            </div>
          ) : (
            <div>
              <Label className="text-xs">Lead source *</Label>
              <Select value={form.lead_source} onValueChange={(v) => setForm((f) => ({ ...f, lead_source: v }))}>
                <SelectTrigger className="mt-1" data-testid="complete-lead-source-select">
                  <SelectValue placeholder="How was this customer acquired?" />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_LIST.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="complete-lead-save-btn" disabled={busy} onClick={save}>
            Save & move to {targetStatus}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
